import { useRef } from 'react'
import type { Timestamp } from '@rundown-studio/utils'
import {
  fastDeepEqual,
  CueRunState,
  GroupRunState,
} from '@rundown-studio/utils'
import type { RunnerTimesnap } from '@rundown-studio/types'
import { addMilliseconds } from 'date-fns'
import { floor50 } from '@/utils'
import { useAtomValue } from 'jotai'
import { getNowAtom } from '@/shared/store/clock.store'
import { runnerAtom } from '../../../store/runner.store'

/**
 * Authoritative group timing information.
 *
 * This object provides a single source of truth for group timing calculations,
 * following the same patterns as createTimestamps from @rundown-studio/utils.
 *
 * @property state - GROUP_PAST | GROUP_ACTIVE | GROUP_FUTURE
 *   - GROUP_PAST: All cues in group have completed
 *   - GROUP_ACTIVE: At least one cue is active (CUE_ACTIVE state)
 *   - GROUP_FUTURE: No cues have started yet
 *
 * @property original - Planned/scheduled timing (from original timestamps)
 *   - Uses original.start and original.duration from first/last timestamps
 *   - Represents the intended timing when rundown was created
 *   - Always available, never changes during show
 *
 * @property actual - Actual/projected timing (from actual timestamps)
 *   - For future groups: matches original (following createTimestamps pattern)
 *   - For started groups: uses actual.start and actual.duration from timestamps
 *   - Always populated (never null), even for future groups
 *
 * @property countdown - Live countdown for active groups (time remaining until group ends)
 *   - When running: actualEnd - now
 *   - When paused: actualEnd - lastStop
 *   - null for non-active groups
 *
 * @property actualDiffersFromOriginal - Whether actual timing differs from original (planned) timing.
 *   - Compares all 3 fields: start, end, duration
 *
 *
 * Key design decisions:
 * - Uses "original" not "planned" to match createTimestamps terminology
 * - actual is always populated (matches original for future groups)
 * - Measures DURATION PERFORMANCE (content time) not SCHEDULE TIMING (early/late)
 */
export type GroupTiming = {
  state: GroupRunState
  original: {
    start: Date
    end: Date
    duration: number
  }
  actual: {
    start: Date
    end: Date
    duration: number
  }
  countdown: number | null
  actualDiffersFromOriginal: boolean
}

/**
 * Calculates authoritative group timing from cue timestamps.
 *
 * This hook provides a stable, cached timing object for a group of cues,
 * calculating start/end times and durations for both original (planned)
 * and actual timing data.
 *
 * ## Caching Strategy
 *
 * Uses fastDeepEqual to compare new vs cached timing objects. Only updates
 * the returned reference if values actually changed. This prevents unnecessary
 * re-renders in downstream components.
 *
 * ## Countdown Field
 *
 * The `countdown` field provides live countdown for active groups, respecting pause state:
 * - When running: actualEnd - now (keeps ticking)
 * - When paused: actualEnd - lastStop (frozen at pause time)
 * - null for non-active groups
 *
 * This follows the same pattern as createMoment's pause handling.
 *
 * ## Important Concepts
 *
 * **Duration Performance vs Schedule Timing:**
 * - This measures DURATION PERFORMANCE: "Are we using more/less content time than planned?"
 * - This does NOT measure schedule timing: "Are we early/late in the schedule?"
 * - A group can run the planned duration but still be late (due to late start/pause)
 * - A group can finish early but use more duration (because it started late)
 *
 * **Example scenarios:**
 *
 * 1. Future group (not started):
 *    - state: GROUP_FUTURE
 *    - original: { start: 10:00, end: 10:30, duration: 1800000 }
 *    - actual: { start: 10:00, end: 10:30, duration: 1800000 } (matches original)
 *
 * 2. Active group (running on time):
 *    - state: GROUP_ACTIVE
 *    - original: { start: 10:00, end: 10:30, duration: 1800000 }
 *    - actual: { start: 10:00, end: 10:28, duration: 1680000 } (projected to finish early)
 *
 * 3. Active group (paused in overtime):
 *    - state: GROUP_ACTIVE
 *    - original: { start: 10:00, end: 10:30, duration: 1800000 }
 *    - actual: { start: 10:00, end: 10:32, duration: 1920000 }
 *    - countdown: -120000 (frozen at -2 minutes when paused)
 *
 * 4. Past group (finished, overran):
 *    - state: GROUP_PAST
 *    - original: { start: 10:00, end: 10:30, duration: 1800000 }
 *    - actual: { start: 10:00, end: 10:35, duration: 2100000 } (used more time than planned)
 *
 * @param groupTimestamps - Array of timestamps for cues in the group (in order)
 * @returns GroupTiming object or null if no timestamps provided
 *
 * @see createTimestamps in @rundown-studio/utils for timestamp structure
 */
export function useGroupTiming(
  groupTimestamps: Timestamp[],
): GroupTiming | null {
  const cacheRef = useRef<GroupTiming | null>(null)
  const getNow = useAtomValue(getNowAtom)
  const runner = useAtomValue(runnerAtom)

  const timesnap = (runner?.timesnap as RunnerTimesnap) ?? null

  // Return null if no timestamps
  if (!groupTimestamps || groupTimestamps.length === 0) {
    return null
  }

  const firstTimestamp = groupTimestamps[0]
  const lastTimestamp = groupTimestamps[groupTimestamps.length - 1]

  // Determine group state
  const groupActive = groupTimestamps.some(
    (ts) => ts.state === CueRunState.CUE_ACTIVE,
  )
  const groupPast = groupTimestamps.every(
    (ts) => ts.state === CueRunState.CUE_PAST,
  )

  const groupState = groupPast
    ? GroupRunState.GROUP_PAST
    : groupActive
      ? GroupRunState.GROUP_ACTIVE
      : GroupRunState.GROUP_FUTURE

  // Calculate times
  const originalStart = firstTimestamp.original.start
  const originalEnd = addMilliseconds(
    lastTimestamp.original.start,
    lastTimestamp.original.duration,
  )
  const originalDuration = floor50(
    originalEnd.getTime() - originalStart.getTime(),
  )
  const actualStart = firstTimestamp.actual.start
  const actualEnd = addMilliseconds(
    lastTimestamp.actual.start,
    lastTimestamp.actual.duration,
  )
  const actualDuration = floor50(actualEnd.getTime() - actualStart.getTime())

  // Calculate countdown for active groups
  let countdown: number | null = null
  if (groupState === GroupRunState.GROUP_ACTIVE && timesnap) {
    // Base countdown: time until group projected to finish
    if (timesnap.running) {
      countdown = actualEnd.getTime() - getNow()
    } else {
      countdown = actualEnd.getTime() - timesnap.lastStop
    }

    // Adjust countdown if active cue is overtime
    // This accounts for being behind schedule on the current cue
    const activeCue = groupTimestamps.find(
      (ts) => ts.state === CueRunState.CUE_ACTIVE,
    )
    if (activeCue) {
      const activeCueOvertime =
        activeCue.actual.duration - activeCue.original.duration
      if (activeCueOvertime > 0) {
        countdown -= activeCueOvertime
      }
    }

    // Apply floor50 to prevent race conditions in countdown display
    countdown = floor50(countdown)
  }

  // Check if actual differs from original (all 3 fields)
  const actualDiffersFromOriginal =
    actualStart.getTime() !== originalStart.getTime() ||
    actualEnd.getTime() !== originalEnd.getTime() ||
    actualDuration !== originalDuration

  // Build new timing object
  const newTiming: GroupTiming = {
    state: groupState,
    original: {
      start: originalStart,
      end: originalEnd,
      duration: originalDuration,
    },
    actual: {
      start: actualStart,
      end: actualEnd,
      duration: actualDuration,
    },
    countdown,
    actualDiffersFromOriginal,
  }

  // Only update cache if values differ (deep comparison)
  const cachedTiming = cacheRef.current
  if (!fastDeepEqual(newTiming, cachedTiming)) {
    cacheRef.current = newTiming
  }

  return cacheRef.current
}
